<template lang="pug">
	.ssml-insertion--say-as-tool
		Popup(v-if="popupVisible", :popupVisible.sync="popupVisible", :width="540", :height="480", :autosize="true", :title="$tCaption('Speech to speech')", @hiding="close")
			.ssml-insertion--say-as-tool.flex-col.space
				.flex-row.align-center.center.space-medium
					.clickable.hoverable.flex-row.align-center.center.adt-button.padded-xlg.record-button.space(@click="toggleRecord", :class="{ recording: recorder.isRecording, 'disabled-controls': recorder.processing }")
						fas(size="lg", :icon="recorder.processing ? 'spinner' : (recorder.isRecording ? 'square' : 'fas fa-microphone fa-xs')", :spin="recorder.processing")
						span {{recorder.processing ? $tCaption('Processing') : $tCaption('Record')}}
					template(v-if="!recorder.processing")
						.clickable.hoverable.flex-row.align-center.center.adt-button.padded-xlg.record-button.space(v-if="sourceBase64", @click="toggleSourcePlay")
							fas(size="lg", :icon="isSourcePlaying === 1 ? 'spinner' : (isSourcePlaying === 2 ? 'square' : 'fas fa-play fa-xs')", :spin="isSourcePlaying === 1")
							span {{isSourcePlaying > 0 ? $tCaption('Stop', '@actions') : $tCaption('Play recorded audio')}}
						.clickable.hoverable.flex-row.align-center.center.adt-button.padded-xlg.record-button.space(v-if="ssmlBase64", @click="togglePlay")
							fas(size="lg", :icon="isPlaying === 1 ? 'spinner' : (isPlaying === 2 ? 'square' : 'fas fa-play fa-xs')", :spin="isPlaying === 1")
							span {{isPlaying > 0 ? $tCaption('Stop', '@actions') : $tCaption('Play generated audio')}}
			template(slot="actions-right")
				DxButton(text="Save", type="default", @click="() => save()")
				DxButton(text="Cancel", @click="() =>{ stopRecording(true); popupVisible = false; }")
			template(slot="actions-left")
				.flex-row.align-center.space-large
					DxCheckBox(:value.sync="recorder.listeningMic.value", :text="$tCaption('Mic preview')")
					VuMeter(ref="vumeter", orientation="horizontal", :isPlaying="recorder.isRecording || recorder.listeningMic.value", :filters="{ stereoAnalyser: recorder.stereoAnalyser }", :compact="true", size="140px")
</template>

<script>
	import _ from 'lodash';
	import Popup from 'app-core/Popup.vue';
	import DxButton from 'devextreme-vue/button';
	import DxCheckBox from 'devextreme-vue/check-box';
	import storage from 'storage';
	import recorderWorker from 'worker-loader!recorder/recorderWorker.js';
	import Recorder from 'recorder/recorder';
	import audioHelper from 'view/audioTracker/audio-helper';
	import { findDefaultVoice } from './cognitive-arrays';
	import VuMeter from 'view/audioTracker/AudioTrackerVUMeter.vue';

	export default {
		translationCategory: 'insertion.SSMLSayAsTool',
		name: 'ssml-insertion--say-as-tool',
		props: ['voice'],
		components: { Popup, DxButton, DxCheckBox, VuMeter },
		data() {
			return {
				popupVisible: false,
				recorder: {
					audioContext: new AudioContext(),
					instance: null,
					stream: null,
					processing: false,
					listeningMic: storage.prop('pronunciation_listeningMic', void 0, false),
					isRecording: false,
					input: null,
					stereoAnalyser: null
				},
				isLoading: 0,
				ssml: null,
				wrappedSsml: null,
				ssmlBase64: null,
				isPlaying: 0,
				sourceBase64: null,
				isSourcePlaying: 0,
				test: false && __DEBUG__,
				testValues: storage.prop('say_as_test_info', void 0, {}),
			}
		},
		computed: {
		},
		watch: {
			'recorder.listeningMic.value'() {
				this.initRecording(!this.recorder.isRecording);
			}
		},
		mounted() {
			if (__DEBUG__)
				window.ssmlSayAsTool = this;
		},
		destroyed() {
			this.stopRecording(true);
		},
		methods: {
			async togglePlay() {
				if (this.isPlaying) {
					this.stopAudio();
					this.isPlaying = 0;
					return;
				}

				try {
					this.isPlaying = 2;
					this.$player = new Audio();
					this.$player.src = this.ssmlBase64;
					this.$player.play();
					this.$player.addEventListener('ended', () => {
						delete this.$player;
						this.isPlaying = 0;
					}, false);
				} catch {
					this.isPlaying = 0;
				}
			},
			async toggleSourcePlay() {
				if (this.isSourcePlaying) {
					this.stopAudio();
					this.isSourcePlaying = 0;
					return;
				}

				try {
					this.isSourcePlaying = 2;
					this.$player = new Audio();
					this.$player.src = this.sourceBase64;
					this.$player.play();
					this.$player.addEventListener('ended', () => {
						delete this.$player;
						this.isSourcePlaying = 0;
					}, false);
				} catch {
					this.isSourcePlaying = 0;
				}
			},
			stopAudio() {
				if (this.$player) {
					this.$player.pause();
				}
			},
			close() {
				this.stopRecording(true);
			},
			save() {
				this.$emit('save', this.ssml, this.wrappedSsml);
				this.popupVisible = false;
			},
			show() {
				if (this.recorder.listeningMic.value) {
					this.initRecording(true);
				}
				this.popupVisible = false;
				this.sourceBase64 = null;
				this.ssml = null;
				this.ssmlBase64 = null;
				setImmediate(() => this.popupVisible = true);
			},
			initRecording(preventRecord) {
				this.stopRecording(true);
				var constraints = { audio: {
					mandatory: {
						googEchoCancellation: false
					},
					optional: []
				}, video: false };
				return new Promise(res => navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
					this.recorder.stream = stream;
					var ctx = this.recorder.audioContext;
					var input = ctx.createMediaStreamSource(stream);

					this.recorder.input = input;

					/* Stereo analyser */
					var stereoAnalyser = new audioHelper.StereoAnalyserNode(ctx);
					stereoAnalyser.fftSize = 512;
					this.recorder.stereoAnalyser = stereoAnalyser;

					if (this.recorder.listeningMic.value) {
						input.connect(stereoAnalyser);
						stereoAnalyser.connect(ctx.destination);
					} else {
						input.connect(stereoAnalyser);
					}

					this.recorder.instance = new Recorder(input, { numChannels: 1, bufferLen: this.$globals.recorderJs.bufferLen }, recorderWorker);
					if (!preventRecord)
						this.recorder.instance.record();

					setImmediate(() => {
						res();
					});
				}).catch((ex) => {
					res();
					throw(ex);
				}));
			},
			async toggleRecord() {
				if (!this.recorder.isRecording && !this.voice?.isActive) {
					this.$confirm(void 0, this.$tCaption('The voice you have selected is in sleep mode.<br>Speech to Speech highly depends on the voice used.<br>You can wait for the voice to come online or use a non-premium voice.'),{
						buttons: [{
							text: this.$tCaption('Wait for voice activation', '@actions'),
							onClick: () => { 
								this.voice.__activate();
							},
							type: 'success'
						}, {
							text: this.$tCaption('Cancel', '@actions'),
							onClick: () => {}
						}]
					});
					return;
				}
				this.recorder.isRecording = !this.recorder.isRecording;
				if (this.recorder.isRecording) {
					this.initRecording();
				} else {
					try {
						this.recorder.processing = true;
						this.ssml = null;
						const blob = await this.stopRecording();
						let voice = this.voice;
						if (voice && !voice?.isActive) {
							voice = findDefaultVoice(voice.locale, voice?.voice?.Gender);
						}
						if (!voice) {
							voice = findDefaultVoice('en-US');
						}
						const result = (await this.recordToSsml(blob, {
							voice: voice.id,
							language: voice.locale
						}).catch(() => {}));
						this.ssml = result?.ssml;
						this.wrappedSsml = result?.wrappedSsml;
						this.ssmlBase64 = result?.ssmlBase64;
						if (__DEBUG__)
							console.debug('Record stop', { blob });

					} finally {
						this.recorder.processing = false;
					}
				}
			},
			async stopRecording(preventSave) {
				this.stopAudio();
				try {
					if (this.recorder.instance) {
						this.recorder.stream.getAudioTracks()[0].stop();
						this.recorder.instance.stop();
						this.recorder.input.disconnect();
					}

					if (!preventSave) {
						return new Promise((res) => this.recorder.instance.exportWAV((blob) => {
							const resampled = audioHelper.resampleBlob(blob, 16000, { mono: true });
							return res(resampled);
						}));
					}
				} catch(ex) {
					console.error(ex);
				}
			},
			clearTestData() {
				this.testValues.value = {};
			},
			async recordToSsml(blob, opts) {
				const sourceBase64 = this.test && this.testValues.value?.base64 ? this.testValues.value?.base64 : await this.$Data.blob2Base64(blob);
				this.sourceBase64 = sourceBase64;

				let result = null;
				if (this.test) {
					if (!this.testValues.value?.base64) {
						this.testValues.value.base64 = sourceBase64;
					}
					if (this.testValues.value?.result) {
						result = this.testValues.value?.result;
					}
				}
					
				if (!result) {
					result = await this.$Data.speechProcessAudio({
						Language: opts?.language || 'en-US',
						TranslateTo: opts?.translateTo,
						ProfileCode: 'SpeechBatchProcessing',
						AllSteps: true
					}, sourceBase64);

					this.testValues.value.result = result;
				}

				const transcript = result?.RecognizedTranscript || [];
				if (!transcript) return;

				const resultPhrases = [];
				let gIdx = 0;
				transcript.map(phrase => {
					const dotPhrases = `${phrase.Text}`.split(/[?\.!]/g).filter(f => f);
					let sentenceIdx = 0;
					const gTextSplit = `${phrase.Text}`.split(' ');
					dotPhrases.map((dotPhrase) => {
						const dotPhraseSplit = dotPhrase.split(' ');
						const resPhrase = {
							words: [],
							text: gTextSplit.slice(sentenceIdx, sentenceIdx + dotPhraseSplit.length).join(' ').replace(/,/g,''),
							texts: gTextSplit.slice(sentenceIdx, sentenceIdx + dotPhraseSplit.length)
						};
						phrase?.Words?.slice(sentenceIdx, sentenceIdx + dotPhraseSplit.length).map((word, wIdx) => {
							const resWord = {
								word: resPhrase.texts[wIdx] || word.Word,
								duration: word.Duration,
								start: word.Offset,
								phrase: resPhrase,
								globalIndex: gIdx + wIdx,
								phraseIndex: sentenceIdx + wIdx
							};
							resWord.timePerChar = resWord.duration / `${resWord.word}`.length;
							resPhrase.words.push(resWord);
						});
						resultPhrases.push(resPhrase);
						resPhrase.startIndex = gIdx;
						resPhrase.endIndex = gIdx + dotPhraseSplit.length;
						resPhrase.duration = resPhrase.words.length <= 1 ? resPhrase.words[0]?.duration : (resPhrase.words[resPhrase.words.length - 1].start - resPhrase.words[0].start + resPhrase.words[resPhrase.words.length - 1].duration); 
						gIdx += dotPhraseSplit.length;
						sentenceIdx += dotPhraseSplit.length;
					});
				});

				const resultWords = resultPhrases.reduce((arr, phrase) => [...arr, ...phrase.words], []);

				if (!resultWords?.length) return;

				/* Detect pitch and pauses */

				const startPause = (resultWords[0]?.start || 0);
				const clearDuration = _.max(resultWords.map(f => f.duration + f.start)) - startPause;
				let lastWord = null;
				resultWords.map(f => {
					if (lastWord) {
						f.startPause = Math.max(0, f.start - (lastWord.start + lastWord.duration));
					}
					lastWord = f;
				});

				const sourceBuffer = await audioHelper.blob2Buffer(await this.$Data.dataURItoBlob(sourceBase64));
				const sourceChannelData = sourceBuffer.getChannelData(0);

				const detectPitchFrequencyOpts = { 
					threshold: 1,
					skipIteration: 1,
					preventThresholdDetection: false
				};

				const getPitchInfo = async (source, start, duration, word) => {
					const checkDur = 0.01;
					const promises = [];
					const count = 100;
					for (var i = 0; i < count; i++) {
						const st = start + i / count * duration;
						if (__DEBUG__)
							console.debug('chk', st, st + checkDur)
						promises.push((async() => {
							return audioHelper.detectPitchFrequency(source.slice(st * audioHelper.ctx.sampleRate, (st + checkDur) * audioHelper.ctx.sampleRate), void 0, detectPitchFrequencyOpts);
						})());
					}
					return Promise.all(promises).then(r => {
						if (__DEBUG__)
							console.debug('Chk results', { r, word });
						var filtered = r.filter(f => f > 0 && f < 800 && f > 70);

						const g0 = filtered.filter(f => f < 101);
						const g1 = filtered.filter(f => f >= 101 && f < 200);
						const g2 = filtered.filter(f => f >= 200 && f < 800);

						filtered = g1.length > 5 ? g1 : (g2 > 20 ? g2 : (g0.length > 10 ? g0 : filtered));

						const uniqValues = filtered.reduce((r, v) => {
							if (!r.some(f => f === v)) {
								r.push(v);
							}
							return r;
						}, []);

						const result = filtered.length ? filtered.reduce((r,v) => r += v, 0) / filtered.length : -1;

						if (__DEBUG__)
							console.debug(`Pitch info (!) ${word} ${result}`, { filtered, g0, g1, g2, uniqValues });

						return result;
					});
				}

				await Promise.all(resultWords.map(async f => {
					const pitchInfo = await getPitchInfo(sourceChannelData, f.start, f.duration, f.word);
					f.pitchInfo = pitchInfo > 0 && pitchInfo < 800 ? [pitchInfo] : [];
				}));

				const allPitches = resultWords.reduce((arr, f) => [...arr, ...(f.pitchInfo || []).filter(f => f < 800)], []);
				let sourceBasePitch = _.sum(allPitches) / allPitches.length;

				resultWords.map(f => {
					f.pitchPercentage = (f.pitchInfo || []).map(f => parseInt((f / sourceBasePitch - 1) * 100)).map(f => Math.max(-20, Math.min(40, f)));
				});

				/* Build ssml */
				let ssmlWordsBoundary = null;
				let ssmlBase64 = null;
				let ssmlWrapper = null;
				let ssmlAudio = null;
				let ssmlPart = null;

				const buildSsml = async () => {
					let ssml = '';

					resultPhrases.map(phrase => {
						const firstWord = phrase.words[0];

						if (firstWord?.startPause) {
							ssml += `<break time="${parseInt(firstWord?.startPause* 1000)}ms" />`;
						}

						const wordsSsml = phrase.words.map((w, wIdx) => {
							return `${wIdx > 0 && w?.ssmlStartPause ? `<break time="${parseInt(w?.ssmlStartPause * 1000)}ms" />` : ''}${w.word}`;
						}).join(' ');

						ssml += `<prosody${phrase.contour || ''} pitch="+0%" rate="${phrase.rate || 1}">${wordsSsml}</prosody>`;
					});

					const language = opts?.language || 'en-US';
					const voice = opts?.voice || 'en-US-JennyNeural';

					ssmlPart = ssml;
					ssmlWrapper = `<speak xmlns="http://www.w3.org/2001/10/synthesis" xmlns:mstts="http://www.w3.org/2001/mstts" xmlns:emo="http://www.w3.org/2009/10/emotionml" version="1.0" xml:lang="${language}">\r\n` +
						`	<voice name="${voice}">\r\n` +
						'   	<mstts:silence type="Leading-exact" value="0ms"/>' +
						'   	<mstts:silence type="Sentenceboundary-exact" value="0ms"/>' +
						`			${ssml}\r\n` +
						'   	<mstts:silence type="Tailing-exact" value="0ms"/>' +
						'	</voice>\r\n' +
						'</speak>';

					const r = await this.$Data.postExternalApi('Creative/BuildAudioCustom', {
						Language: language,
						Rate: 1,
						Pitch: 0,
						Voice: voice,
						Ssml: ssmlWrapper,
						ClientProcessId: this.$auth.getGlobalProcessId()
					}).catch(() => {});

					ssmlAudio = {
						base64: r?.AudioData && `data:audio/wav;base64,${r?.AudioData}`,
						insertion: r?.SsmlInsertion,
						hasDefaultVoiceReplaced: false
					}

					ssmlWordsBoundary = (ssmlAudio?.insertion?.JsonConfig?.wordsBoundary || []).map(f => ({
						start: f.ao / 1000,
						duration: f.ad / 1000,
						textOffset: f.to,
						wordsLength: f.wl,
						word: f.t,
						timePerChar: f.ad / 1000 / `${f.t}`.length
					}));

					ssmlBase64 = ssmlAudio?.base64;
				}

				await buildSsml();

				const ssmlWordsBoundaryPitches = ssmlWordsBoundary?.map(f => ({ ...f })) || [];
				if (ssmlWordsBoundaryPitches?.length) {
					const ssmlBuffer = await audioHelper.blob2Buffer(await this.$Data.dataURItoBlob(ssmlBase64));
					const ssmlChannelData = ssmlBuffer.getChannelData(0);

					/* Calculate pitch info for ssml */
					await Promise.all(ssmlWordsBoundaryPitches.map(async f => {
						const pitchInfo = await getPitchInfo(ssmlChannelData, f.start, f.duration, f.word);
						f.pitchInfo = pitchInfo > 0 && pitchInfo < 800 ? [pitchInfo] : [];
					}));

					const ssmlPitches = ssmlWordsBoundaryPitches.reduce((arr, f) => [...arr, ...f.pitchInfo.filter(f => f < 800)], []);
					let ssmlBasePitch = _.sum(ssmlPitches) / ssmlPitches.length;

					ssmlWordsBoundaryPitches.map(f => {
						f.pitchPercentage = f.pitchInfo.map(f => parseInt((f / ssmlBasePitch - 1) * 100)).map(f => Math.max(-20, Math.min(40, f)));
					});

					/* Calculate pauses diff between real pauses and ssml words */
					resultPhrases.map(phrase => {
						const ssmlWords = ssmlWordsBoundary.slice(phrase.startIndex, phrase.endIndex);
						phrase.words.map((f, i) => {
							const lastSsmlWord = ssmlWords[i-1];
							const ssmlWord = ssmlWords[i];
							f.ssmlWord = ssmlWord;
							if (ssmlWord && lastSsmlWord) {
								const ssmlPause = ssmlWord.start - lastSsmlWord.start - lastSsmlWord.duration;
								const diff = Math.max(0, (f.startPause - ssmlPause) || 0);
								f.ssmlStartPause = diff;
							}
						});
					});
					await buildSsml();

					if (ssmlWordsBoundary?.length) {
						resultPhrases.map(phrase => {
							const ssmlWords = ssmlWordsBoundary.slice(phrase.startIndex, phrase.endIndex);
							const ssmlWordsPitches = ssmlWordsBoundaryPitches.slice(phrase.startIndex, phrase.endIndex);
							const phraseClearDuration = phrase.words.reduce((total, w) => total + w.duration, 0);
							const ssmlClearDuration = ssmlWords.reduce((total, w) => total + w.duration, 0);
							const phraseAvgPerChar = phrase.words.reduce((total, w) => total + w.timePerChar, 0) / phrase.words.length;
							const ssmlAvgPerChar = ssmlWords.reduce((total, w) => total + w.timePerChar, 0) / ssmlWords.length;
							const ssmlDuration = _.max(ssmlWords.map(f => f.start + f.duration));

							/* Calculate contour */
							const contour = [];
							let pitchCounter = 0;
							const pitchTotal = phrase.words.reduce((total, w) => {
								w.pitchPercentage.map(p => total += p);
								pitchCounter += w.pitchPercentage.length;
								return total;
							}, 0);
							const contourFix = (pitchTotal / pitchCounter) || 0;
							let lastPitch = 0;
							let lastDurationPosition = 0;
							phrase.words.map((f, i) => {
								const ssmlWord = ssmlWords[i];
								const ssmlPitches = ssmlWordsPitches[i];
								if (ssmlWord) {
									const pitches = f.pitchPercentage?.length ? f.pitchPercentage : [lastPitch];
									pitches.map((value, pIdx) => {
										const pitchPosition = ssmlWord.start + (pIdx / pitches.length) * ssmlWord.duration;
										const durationPercentage = Math.max(0, Math.min(100, parseInt(pitchPosition / ssmlDuration * 100)));
										const resValue = value - contourFix - (ssmlPitches.pitchPercentage && (ssmlPitches.pitchPercentage[pIdx] || ssmlPitches.pitchPercentage[0]) || 0);
										if (durationPercentage > lastDurationPosition) {
											contour.push(`(${lastDurationPosition === 0 ? 0 : durationPercentage}%, ${resValue >= 0 ? '+' : ''}${resValue}%)`);
											lastPitch = value;
											lastDurationPosition = durationPercentage;
										}
									});
								}
							});

							if (contour.length) {
								phrase.contour = ` contour="${contour.join(' ')}"`;
							}

							/* Calculate phrase rate */
							const perCharRate = ssmlAvgPerChar / phraseAvgPerChar;
							phrase.rate = Math.max(0.7, Math.min(1.3, perCharRate));

							if (__DEBUG__)
								console.debug('Record to ssml phrase rate', { phrase, rate: phrase.rate, ssmlClearDuration, phraseClearDuration, ssmlWords, ssmlAvgPerChar, phraseAvgPerChar, contourFix, ssmlWordsBoundaryPitches })
						});
						await buildSsml();
					}
				}

				let pitchInfo = { sourceBasePitch };

				if (__DEBUG__)
					console.debug('Record to ssml', { 
						source: { blob, result, resultWords, clearDuration, startPause, resultPhrases }, 
						ssml: { ssmlWrapper, ssmlWordsBoundary },
						buffers: { sourceBuffer },
						pitchInfo
					});

				const res = {
					ssml: ssmlPart,
					wrappedSsml: ssmlWrapper,
					ssmlBase64,
					sourceBase64,
					resultWords,
					ssmlWordsBoundary,
					transcript,
					pitchInfo
				}

				if (__DEBUG__)
					window.sayAsResult = res;

				return res;
			}
		}
	}
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.ssml-insertion--say-as-tool {
		.record-button {
			border-radius: 4px;
		}
	}
</style>
